import React from 'react';
import { getFoodProfileAPI, saveFoodProfileAPI } from '../utils/api';

export const FOOD_PROFILE_STORAGE_KEY = 'vegai.foodProfile.v1';

export const FOOD_PROFILE_PRESETS = [
  'vegan',
  'vegetarian',
  'dairy_free',
  'gluten_aware',
  'allergy_watch',
];

export const FOOD_PROFILE_ALLERGENS = [
  'milk',
  'egg',
  'peanut',
  'tree_nut',
  'soy',
  'wheat',
  'sesame',
  'fish',
  'shellfish',
];

const DEFAULT_PROFILE = {
  presets: ['vegan'],
  watchedAllergens: [],
  avoidedIngredients: [],
};

export const MAX_AVOIDED_INGREDIENTS = 25;
export const MAX_AVOIDED_INGREDIENT_LENGTH = 50;

function importPromptKey(userId) {
  return `vegai.foodProfileImportPrompt.v1.${userId}`;
}

function cleanList(value, allowed, fallback = []) {
  if (!Array.isArray(value)) return [...fallback];
  return [...new Set(
    value
      .map((item) => String(item || '').trim().toLowerCase())
      .filter((item) => allowed.includes(item))
  )];
}

function cleanAvoidedIngredients(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const cleaned = [];
  for (const raw of value) {
    const item = String(raw || '').replace(/\s+/g, ' ').trim().slice(0, MAX_AVOIDED_INGREDIENT_LENGTH);
    if (!item) continue;
    const key = item.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(item);
    if (cleaned.length >= MAX_AVOIDED_INGREDIENTS) break;
  }
  return cleaned;
}

export function normalizeFoodProfile(value) {
  const raw = value && typeof value === 'object' ? value : {};
  return {
    presets: cleanList(raw.presets, FOOD_PROFILE_PRESETS, DEFAULT_PROFILE.presets),
    watchedAllergens: cleanList(raw.watchedAllergens, FOOD_PROFILE_ALLERGENS),
    avoidedIngredients: cleanAvoidedIngredients(raw.avoidedIngredients),
    ...(raw.updatedAt ? { updatedAt: String(raw.updatedAt) } : {}),
  };
}

function readLocalProfile() {
  try {
    const raw = localStorage.getItem(FOOD_PROFILE_STORAGE_KEY);
    if (!raw) return { profile: normalizeFoodProfile(DEFAULT_PROFILE), exists: false };
    return { profile: normalizeFoodProfile(JSON.parse(raw)), exists: true };
  } catch {
    return { profile: normalizeFoodProfile(DEFAULT_PROFILE), exists: false };
  }
}

function writeLocalProfile(profile) {
  localStorage.setItem(
    FOOD_PROFILE_STORAGE_KEY,
    JSON.stringify({
      presets: profile.presets,
      watchedAllergens: profile.watchedAllergens,
      avoidedIngredients: profile.avoidedIngredients,
    })
  );
}

export function useFoodProfile({ user, session } = {}) {
  const token = session?.access_token || '';
  const userId = user?.id || '';
  const signedIn = Boolean(userId && token);
  const initialLocal = React.useMemo(() => readLocalProfile(), []);
  const localProfileRef = React.useRef(initialLocal.profile);
  const localProfileExistsRef = React.useRef(initialLocal.exists);
  const profileRef = React.useRef(initialLocal.profile);
  const saveTimerRef = React.useRef(null);
  const saveSequenceRef = React.useRef(0);
  const [profile, setProfile] = React.useState(initialLocal.profile);
  const [loading, setLoading] = React.useState(false);
  const [saveState, setSaveState] = React.useState('saved_device');
  const [error, setError] = React.useState('');
  const [loadedUserId, setLoadedUserId] = React.useState('');
  const [importOffer, setImportOffer] = React.useState(false);
  const [importing, setImporting] = React.useState(false);

  React.useEffect(() => () => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
  }, []);

  React.useEffect(() => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveSequenceRef.current += 1;
    if (!signedIn) {
      const local = readLocalProfile();
      localProfileRef.current = local.profile;
      localProfileExistsRef.current = local.exists;
      profileRef.current = local.profile;
      setProfile(local.profile);
      setLoading(false);
      setSaveState('saved_device');
      setError('');
      setLoadedUserId('');
      setImportOffer(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSaveState('loading');
    setError('');
    getFoodProfileAPI(token)
      .then((data) => {
        if (cancelled) return;
        const nextProfile = normalizeFoodProfile(data.profile);
        profileRef.current = nextProfile;
        setProfile(nextProfile);
        setLoadedUserId(userId);
        setSaveState('saved_account');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Could not load your Food Profile.');
        setSaveState('error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn, token, userId]);

  React.useEffect(() => {
    if (!signedIn || loadedUserId !== userId || !localProfileExistsRef.current) return;
    try {
      if (localStorage.getItem(importPromptKey(userId)) === 'done') return;
    } catch {
      return;
    }
    setImportOffer(true);
  }, [signedIn, loadedUserId, userId]);

  const markImportPromptDone = React.useCallback(() => {
    try {
      localStorage.setItem(importPromptKey(userId), 'done');
    } catch {
      // The choice still applies for this page if browser storage is blocked.
    }
    setImportOffer(false);
  }, [userId]);

  const saveSignedInProfile = React.useCallback((nextProfile) => {
    if (!signedIn) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    const sequence = ++saveSequenceRef.current;
    setSaveState('saving');
    setError('');
    saveTimerRef.current = window.setTimeout(() => {
      saveFoodProfileAPI(token, nextProfile)
        .then((data) => {
          if (sequence !== saveSequenceRef.current) return;
          const savedProfile = normalizeFoodProfile(data.profile);
          profileRef.current = savedProfile;
          setProfile(savedProfile);
          setSaveState('saved_account');
        })
        .catch((err) => {
          if (sequence !== saveSequenceRef.current) return;
          setError(err?.message || 'Could not save your Food Profile.');
          setSaveState('error');
        });
    }, 350);
  }, [signedIn, token]);

  const updateProfile = React.useCallback((updater) => {
    const current = profileRef.current;
    const nextValue = typeof updater === 'function' ? updater(current) : updater;
    const nextProfile = normalizeFoodProfile(nextValue);
    profileRef.current = nextProfile;
    setProfile(nextProfile);
    if (signedIn) {
      saveSignedInProfile(nextProfile);
    } else {
      try {
        writeLocalProfile(nextProfile);
        localProfileRef.current = nextProfile;
        localProfileExistsRef.current = true;
        setSaveState('saved_device');
        setError('');
      } catch {
        setSaveState('error');
        setError('This browser could not save your Food Profile.');
      }
    }
  }, [signedIn, saveSignedInProfile]);

  const togglePreset = React.useCallback((preset) => {
    updateProfile((current) => ({
      ...current,
      presets: current.presets.includes(preset)
        ? current.presets.filter((item) => item !== preset)
        : [...current.presets, preset],
    }));
  }, [updateProfile]);

  const toggleAllergen = React.useCallback((allergen) => {
    updateProfile((current) => ({
      ...current,
      watchedAllergens: current.watchedAllergens.includes(allergen)
        ? current.watchedAllergens.filter((item) => item !== allergen)
        : [...current.watchedAllergens, allergen],
    }));
  }, [updateProfile]);

  const addAvoidedIngredient = React.useCallback((ingredient) => {
    const cleaned = String(ingredient || '').replace(/\s+/g, ' ').trim().slice(0, MAX_AVOIDED_INGREDIENT_LENGTH);
    if (!cleaned) return;
    updateProfile((current) => {
      const existing = current.avoidedIngredients || [];
      if (existing.some((item) => item.toLowerCase() === cleaned.toLowerCase())) return current;
      return {
        ...current,
        avoidedIngredients: [...existing, cleaned].slice(0, MAX_AVOIDED_INGREDIENTS),
      };
    });
  }, [updateProfile]);

  const removeAvoidedIngredient = React.useCallback((ingredient) => {
    const key = String(ingredient || '').trim().toLowerCase();
    if (!key) return;
    updateProfile((current) => ({
      ...current,
      avoidedIngredients: (current.avoidedIngredients || []).filter((item) => item.toLowerCase() !== key),
    }));
  }, [updateProfile]);

  const importLocalProfile = React.useCallback(async () => {
    if (!signedIn || !localProfileExistsRef.current) return;
    setImporting(true);
    setSaveState('saving');
    try {
      const data = await saveFoodProfileAPI(token, localProfileRef.current);
      const importedProfile = normalizeFoodProfile(data.profile);
      profileRef.current = importedProfile;
      setProfile(importedProfile);
      setSaveState('saved_account');
      setError('');
      markImportPromptDone();
    } catch (err) {
      setError(err?.message || 'Could not import this device Food Profile.');
      setSaveState('error');
      throw err;
    } finally {
      setImporting(false);
    }
  }, [signedIn, token, markImportPromptDone]);

  return {
    profile,
    loading,
    saveState,
    error,
    importOffer,
    importing,
    togglePreset,
    toggleAllergen,
    addAvoidedIngredient,
    removeAvoidedIngredient,
    importLocalProfile,
    dismissImport: markImportPromptDone,
  };
}
